import { useMemo, useState } from 'react';
import { useTabState } from '../lib/tabParam';
import { useGroupCount, groupTabLabel } from '../lib/groups';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiClientError } from '../lib/api';
import { billYearLabel } from '../lib/billYear';
import { valueFaceLabel } from '../lib/valueLabel';
import { latestAttr, registryOptions, humanizeToken, type AttrMap } from '../lib/inlineAttribution';
import { STATE_SELECT_OPTIONS } from '../lib/us-states';
import { Badge, Button, Checkbox, IconPencil } from '../atoms';
import { useIsCompact } from '../lib/useMediaQuery';
import { Tabs, DataTable, StatCard, CreateEntityModal, AddValueDialog, InlineEditCard, FileAppealModal, RecordOutcomeModal, EntityActivityFeed, CustomFieldsPanel, RelatedParcelsPanel, EntityContactsPanel, EntityGroupsPanel, EntityNotesPanel, FollowButton, ArchiveButton, LastUpdated, PropertyMapCard, GenerateEmailModal, useToast } from '../organisms';
import type { AddValueAppealOption } from '../organisms';
import { EntityHeader } from '../shell';
import { useEntityPermission } from '../lib/usePermissions';
import { ParcelValuationTab } from './ParcelValuationTab';
import { stageLabel, stageVariant, isTerminalStage } from '../lib/appeal-stages';
import { formatDate } from '../lib/orgTimezone';

/**
 * ParcelDetail — /v2/parcels/:id
 *
 * EntityHeader (prefix=account_number; title=name/owner; status pill;
 * under-appeal pill if applicable; KPI row) above a Tabs panel: Overview
 * · Values · Tax Bills · Tax Records.
 *
 * Phase 11 Block 7 PR 5 (2026-05-12).
 */

interface Parcel {
  id: string;
  account_number: string;
  alternate_id: string | null;
  name: string | null;
  property_id: string;
  assessor_id: string;
  consultant_user_id?: string | null;
  consultant_id?: string | null;
  associate_user_id?: string | null;
  assistant_user_id?: string | null;
  status: 'active' | 'inactive' | 'in_setup' | 'historical' | 'sold' | 'sold_after_value' | 'unknown' | 'other';
  re_pp: 'real_estate' | 'personal_property';
  under_appeal: boolean;
  archived_at?: string | null; // §9
  appeal_date: string | null;
  tracking_number: string | null;
  owner_name: string | null;
  legal_description: string | null;
  // The parcel's county — the grain at which boundary-tile coverage arrives.
  county: string | null;
  // The JURISDICTIONAL state — what the appeal ladder resolves off. Distinct from
  // parcel_state below, which is part of the mailing-address block.
  state: string | null;
  address_line1: string | null;
  parcel_city: string | null;
  parcel_state: string | null;
  parcel_postal_code: string | null;
  current_market_value: number | null;
  current_assessed_value: number | null;
  proposed_market_value: number | null;
  // Exemption-eligibility flags (FS-1). Set directly — the owner-occupancy gate
  // that used to guard them was removed with its column (Y10, migration 146).
  // Booleans arrive as 0/1 over the wire (pg-wrapper normalization).
  homestead: boolean | number | null;
  over_65: boolean | number | null;
  year: number;
  updated_at?: string | null;
  updated_by?: string | null;
}

interface ParcelValue {
  id: string;
  tax_year: number;
  value_type: string;
  status: string | null;
  market_total: number | null;
  assessed_total: number | null;
  is_beginning_value: boolean | number | null;
  is_current_value: boolean | number | null;
  // Appeal-lifecycle respec §2.1 (migration 130) — a value row IS an appeal event.
  // These are NULL on every legacy row (the columns are new + empty), so every
  // reader below degrades gracefully (forum/event fall back to the value_type).
  appeal_id?: string | null;
  forum?: string | null;
  event?: string | null;
  date?: string | null;
  value_source?: string | null;
  // §2.8 — a SECOND figure carried on the same value row (an alternate basis: a
  // pre-abatement market value, a Freeport or phase-in figure). `alternate_value_type`
  // NAMES it, and is what the row is labelled with, so the operator reads
  // "Market value 3,261,580" rather than a generic caption. It is deliberately not
  // folded into market/assessed: those are the assessor's figures for the year.
  alternate_value?: number | null;
  alternate_value_type?: string | null;
  // Granular components for the expanded box face (also NULL-safe).
  market_land?: number | null;
  market_imp?: number | null;
  market_pp?: number | null;
  ratio_land?: number | null;
  ratio_imp?: number | null;
  ratio_pp?: number | null;
  assessed_land?: number | null;
  assessed_imp?: number | null;
  assessed_pp?: number | null;
}

/**
 * The parcel's canonical appeal record (one per parcel per tax year). Read via
 * GET /api/appeals?parcel_id= (read-only CRUD; writes go through the workflow
 * endpoints that also log appeal_stage_events). Drives the lifecycle timeline on
 * the Values tab — the "whole history of the appeal progress" per the demo ask.
 */
interface Appeal {
  id: string;
  parcel_id: string | null;
  tax_year: number | null;
  lifecycle_stage: string;
  resolution_path: string | null;
  value_notice_date: string | null;
  protest_filed_date: string | null;
  hearing_notice_date: string | null;
  hearing_date: string | null;
  affidavit_sent_date: string | null;
  board_order_expected_date: string | null;
  board_order_received_date: string | null;
  litigation_deadline_date: string | null;
  litigation_filed_date: string | null;
  closed_date: string | null;
  outcome: string | null;
  settled_value: number | null;
  reduction: number | null;
  // §2.2 (migration 130) — the years a 299(c)/carried appeal covers, stored as a
  // TEXT JSON array ('[2024,2025,2026]'); NULL on legacy rows. A covered appeal
  // fans onto every covered year's card.
  covered_tax_years?: string | null;
}

interface Reduction {
  id: string;
  tax_year: number | null;
  type: string;
  status: string | null;
  amount: number | null;
  percentage: number | null;
}

interface TaxBill {
  id: string;
  tax_year: number;
  process_year: number | null; // RH§1 — the payable year (Siger "Process")
  // The bill's total tax owed. Column is `tax_amount` (= SUM of the parcel's
  // tax_records.tax_amount); reading `amount` left every bill amount blank.
  tax_amount: number | null;
  due_date: string | null;
  paid_date: string | null;
  status: string;
}

interface TaxRecord {
  id: string;
  tax_year: number;
  jurisdiction_id: string;
  rate: number | null;
  // The tax this jurisdiction line contributes (taxable_value × rate ÷ divisor).
  // The bill's amount due is the SUM of these across the parcel's records.
  tax_amount: number | null;
  process_year: number | null;
  status: string;
  // RH§1 — TRUE while the rate is pulled from an UNCONFIRMED jurisdiction rate:
  // the line is an ESTIMATE until the jurisdiction's rate is finalized. (pg returns
  // booleans as 0/1.)
  rate_is_estimate?: boolean | number | null;
  // The two ends of the appeal's measurement on THIS line. `beginning_value` is the
  // figure the saving is measured FROM (and honours the line's own "use alternate
  // value" tick); `current_value` is what it settled at. Both are in the money
  // denylist, so a money-blind role receives neither and the savings column reads
  // "—" for them without any special case here.
  beginning_value?: number | null;
  current_value?: number | null;
}

/** RH§1 — pg serializes booleans as 0/1; treat either truthy form as an estimate. */
function isEstimateLine(v: boolean | number | null | undefined): boolean {
  return v === true || v === 1;
}

/** The parcel's BPP return log entry (§6.2 — return ↔ parcel stay linked). */
interface LinkedReturn {
  id: string;
  tax_year: number;
  status: string;
  due_date: string | null;
  reported_amount: number | null;
}

const statusVariant: Record<Parcel['status'], 'success' | 'warning' | 'neutral' | 'info'> = {
  active: 'success', inactive: 'neutral', in_setup: 'warning', historical: 'info',
  sold: 'neutral', sold_after_value: 'neutral', unknown: 'neutral', other: 'neutral',
};

export function ParcelDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // §1.2 + §1.3 — 'reductions' and 'phasein' are GONE as parcel-profile tabs.
  // Exemptions live on the parcel value profile and fold into the Values tab;
  // phase-in and 299(c) moved to the parcel value alongside them. A stale
  // `?tab=reductions` / `?tab=phasein` deep link is not in this list, so
  // initialTabFromUrl drops it to Overview rather than rendering nothing.
  const [tab, setTab] = useTabState(['overview', 'map', 'values', 'bills', 'records', 'valuation', 'contacts', 'groups', 'notes', 'documents', 'activity', 'custom', 'related'] as const, 'overview');
  const groupCount = useGroupCount('parcel', id);
  const [editOpen, setEditOpen] = useState(false);
  const [fileAppealOpen, setFileAppealOpen] = useState(false);
  const [recordOutcomeOpen, setRecordOutcomeOpen] = useState(false);
  // Values-tab entry (respec §4.2): "+ Add value" opens the bespoke AddValueDialog
  // (appeal + forum/event + both-direction grid); editing a value opens its card
  // (ParcelValueDetail, §4.3), which is also where exemptions are authored.
  const [addValueOpen, setAddValueOpen] = useState(false);
  // ATA part 3 §7 (item 27): the template dropdown at the appeal stage on the parcel lifecycle.
  const [emailAppeal, setEmailAppeal] = useState<Appeal | null>(null);
  const onEmailAppeal = (a: Appeal) => setEmailAppeal(a);
  // Write affordances render only for holders of parcel edit (SoD: render the
  // model, never soften it). Default-deny while the grid loads.
  const canEditParcel = useEntityPermission('parcel', 'can_edit');
  // File-appeal / Record-outcome POST to /api/workflow/* which authorize on the
  // appeal PROCESS capability (not parcel.can_edit) — gate on the same bit so the
  // button shows iff the server would accept the submit.
  const canProcessAppeal = useEntityPermission('appeal', 'can_process');

  const parcelQuery = useQuery<Parcel>({
    queryKey: ['parcel', id],
    queryFn: ({ signal }) => api.get<Parcel>(`/api/parcels/${id}`, signal),
    enabled: !!id,
  });

  const valuesQuery = useQuery<ParcelValue[]>({
    queryKey: ['parcel-values', id],
    queryFn: ({ signal }) => api.get<ParcelValue[]>(`/api/parcel-values?parcel_id=${id}`, signal),
    enabled: !!id,
  });

  // Still read even though the standalone tab is gone (§1.2): the Values tab folds
  // each year's exemptions into that year's card.
  const reductionsQuery = useQuery<Reduction[]>({
    queryKey: ['reductions-by-parcel', id],
    queryFn: ({ signal }) => api.get<Reduction[]>(`/api/reductions?parcel_id=${id}`, signal),
    enabled: !!id,
  });

  // The parcel's appeal record(s) — one per tax year — power the Values tab's
  // appeal-progress lifecycle timeline. Read-only through generic CRUD.
  const appealsQuery = useQuery<Appeal[]>({
    queryKey: ['appeals-by-parcel', id],
    queryFn: ({ signal }) => api.get<Appeal[]>(`/api/appeals?parcel_id=${id}`, signal),
    enabled: !!id,
  });

  const billsQuery = useQuery<TaxBill[]>({
    queryKey: ['tax-bills-by-parcel', id],
    queryFn: ({ signal }) => api.get<TaxBill[]>(`/api/tax-bills?parcel_id=${id}`, signal),
    enabled: !!id,
  });

  const recordsQuery = useQuery<TaxRecord[]>({
    queryKey: ['tax-records-by-parcel', id],
    queryFn: ({ signal }) => api.get<TaxRecord[]>(`/api/tax-records?parcel_id=${id}`, signal),
    enabled: !!id,
  });

  // Jurisdiction id → name, so the unified Tax Bills view can label each
  // jurisdiction line (tax record) that composes the bill.
  const jurisdictionsQuery = useQuery<{ id: string; name: string }[]>({
    queryKey: ['jurisdictions-lookup'],
    queryFn: ({ signal }) => api.get<{ id: string; name: string }[]>('/api/jurisdictions', signal),
    staleTime: 5 * 60_000,
  });
  // id → name map for the unified Tax Bills view. This useMemo MUST sit with the
  // other hooks (before the loading/error early returns) to keep hook order stable.
  const jurisdictionNames = useMemo(
    () => Object.fromEntries((jurisdictionsQuery.data ?? []).map((j) => [j.id, j.name] as const)),
    [jurisdictionsQuery.data],
  );

  // §6.2 — a PP parcel's BPP return is visible from the parcel. Only PP parcels
  // carry a return, so gate the fetch on the designation to avoid a needless call.
  const isPersonalProperty = parcelQuery.data?.re_pp === 'personal_property';
  const returnsQuery = useQuery<LinkedReturn[]>({
    queryKey: ['returns-by-parcel', id],
    queryFn: ({ signal }) => api.get<LinkedReturn[]>(`/api/filed-returns?primary_parcel_id=${id}`, signal),
    enabled: !!id && isPersonalProperty,
  });

  if (parcelQuery.isLoading) {
    return <p style={{ color: 'var(--color-ink-3)' }}>Loading parcel…</p>;
  }

  if (parcelQuery.error) {
    const notFound = parcelQuery.error instanceof ApiClientError && parcelQuery.error.status === 404;
    return (
      <div style={{
        padding: 'var(--sp-5)',
        background: notFound ? 'var(--color-card)' : 'var(--color-status-red-bg)',
        border: '1px solid ' + (notFound ? 'var(--color-border)' : 'var(--color-status-red)'),
        color: notFound ? 'var(--color-ink)' : 'var(--color-status-red)',
        borderRadius: 'var(--r-lg)',
      }}>
        <h2 style={{ margin: '0 0 var(--sp-2)' }}>{notFound ? 'Parcel not found' : 'Failed to load parcel'}</h2>
        <p style={{ margin: 0 }}>{(parcelQuery.error as Error).message}</p>
        <p style={{ marginTop: 'var(--sp-3)' }}>
          <Button variant="secondary" size="sm" onClick={() => navigate('/parcels')}>
            Back to Parcels
          </Button>
        </p>
      </div>
    );
  }

  if (!parcelQuery.data) return null;

  const parcel = parcelQuery.data;
  const values = valuesQuery.data ?? [];
  const reductions = reductionsQuery.data ?? [];
  const appeals = appealsQuery.data ?? [];
  const bills = billsQuery.data ?? [];
  // The distinct tax years the values tab shows. Drives the bills tab's "no bill on file"
  // rows so the two tabs can never disagree about which years this parcel has activity in.
  //
  // ⚠️ DELIBERATELY NOT a useMemo. This sits BELOW the component's early returns, so a hook
  // here runs on some renders and not others — React counts a different number of hooks
  // between renders and throws "Rendered more hooks than during the previous render",
  // white-screening the page. (It did; the hooks test caught it.) The computation is a map
  // over a handful of rows, so memoising it buys nothing and costs the ordering hazard.
  const valueYears = [...new Set(values.map((v) => v.tax_year).filter((y): y is number => Number.isFinite(y)))];
  const records = recordsQuery.data ?? [];

  // Realized reduction (respec §3.1): savings are the baseline value minus the
  // FINAL value — realized only, never a projection against a target. The old
  // "$X off current" projection KPI is dropped. Computed for the parcel's current
  // year; null (→ "No final yet") until a final value lands.
  const currentYearValues = values.filter((v) => v.tax_year === parcel.year);
  // The record a NEW stage inherits its figures from. Chosen explicitly rather than taking
  // the first row: `values` carries whatever order the API returned, and "most recent" is a
  // claim that has to be computed, not assumed. Falls back through date → id so the pick is
  // deterministic even when dates tie or are absent (`date` is the only time field the
  // row carries here).
  const priorValueForNewStage = currentYearValues.length
    ? [...currentYearValues].sort((a, b) => {
        const t = (v: ParcelValue) => Date.parse(String(v.date ?? '')) || 0;
        return t(b) - t(a) || String(b.id).localeCompare(String(a.id));
      })[0]
    : null;
  const baselineValue = currentYearValues.find((v) => isOn(v.is_beginning_value)) ?? null;
  const finalValue = currentYearValues.find((v) => v.value_type === 'final') ?? null;
  const realizedReduction =
    baselineValue && finalValue && baselineValue.assessed_total != null && finalValue.assessed_total != null
      ? Math.max(0, baselineValue.assessed_total - finalValue.assessed_total)
      : null;

  const latestBill = bills.length > 0
    ? bills.reduce((a, b) => (a.tax_year > b.tax_year ? a : b))
    : null;

  // §5 — the appeal state a person reads is DERIVED from the appeal records, not
  // taken off `parcels.under_appeal`. That flag is the legacy operational boolean:
  // the workflow maintains it, but a seeded or imported parcel can carry an appeal
  // row with the flag never set — which is how a header came to read "Not yet
  // appealed" directly above an appeal that plainly exists.
  //
  // ⚠️ The flag is OR-ed in rather than dropped, deliberately. A parcel flagged
  // under appeal with no appeal row is the OTHER stale case, and reading the records
  // alone would newly hide it. Trusting either witness only ever ADDS evidence that
  // an appeal exists, so this can't invent the "nothing here" reading the §5 note is
  // about.
  const openAppeal = appeals.find((a) => !isTerminalStage(a.lifecycle_stage)) ?? null;
  const latestAppeal = appeals.length > 0
    ? appeals.reduce((a, b) => ((a.tax_year ?? 0) >= (b.tax_year ?? 0) ? a : b))
    : null;
  const isUnderAppeal = !!openAppeal || !!parcel.under_appeal;
  const appealCaption = openAppeal || parcel.under_appeal
    ? 'Under appeal'
    : latestAppeal
      ? stageLabel(latestAppeal.lifecycle_stage)
      : 'Not yet appealed';

  return (
    <>
      <EntityHeader
        prefix={`Parcel ${parcel.account_number}`}
        title={parcel.name || parcel.owner_name || 'Unnamed parcel'}
        subtitle={
          <>
            {[parcel.address_line1, parcel.parcel_city, parcel.parcel_state].filter(Boolean).join(', ') || null}
            <LastUpdated updatedBy={parcel.updated_by} updatedAt={parcel.updated_at} />
          </>
        }
        statusPills={
          <>
            <Badge variant={statusVariant[parcel.status]} dot>{parcel.status.replace(/_/g, ' ')}</Badge>
            {parcel.re_pp === 'personal_property' && (
              <Badge variant="info" dot>Personal property (BPP)</Badge>
            )}
            {isUnderAppeal && (
              <Badge variant="warning" dot>Under appeal</Badge>
            )}
            {parcel.archived_at && <Badge variant="neutral">Archived</Badge>}
          </>
        }
        actions={
          <>
            {canEditParcel && (
              <Button variant="secondary" size="sm" onClick={() => setEditOpen(true)}>
                Edit all fields
              </Button>
            )}
            {canProcessAppeal && !isUnderAppeal && (
              <Button variant="primary" size="sm" onClick={() => setFileAppealOpen(true)}>
                File appeal
              </Button>
            )}
            {canProcessAppeal && isUnderAppeal && (
              <Button variant="primary" size="sm" onClick={() => setRecordOutcomeOpen(true)}>
                Record outcome
              </Button>
            )}
            <FollowButton entityType="parcel" entityId={parcel.id} />
            {/* §9 — archive / unarchive (super-admin + office administrator). */}
            <ArchiveButton
              entityType="parcels"
              entityId={parcel.id}
              archived={!!parcel.archived_at}
              invalidateKeys={[['parcel', parcel.id], ['client-view'], ['parcels-list']]}
            />
          </>
        }
        kpis={
          <>
            <StatCard
              label="Current market value"
              value={parcel.current_market_value != null ? `$${parcel.current_market_value.toLocaleString()}` : '—'}
              caption={`Tax year ${parcel.year}`}
              accent="hunter"
            />
            <StatCard
              label="Proposed value"
              value={parcel.proposed_market_value != null ? `$${parcel.proposed_market_value.toLocaleString()}` : '—'}
              caption={appealCaption}
              accent="info"
            />
            {/* Realized reduction only (§3.1) — no projection against a target. */}
            <StatCard
              label="Realized reduction"
              value={realizedReduction != null ? `$${realizedReduction.toLocaleString()}` : '—'}
              caption={realizedReduction != null ? 'Assessed, at final' : 'No final yet'}
              accent={realizedReduction != null && realizedReduction > 0 ? 'green' : 'amber'}
            />
            <StatCard
              label="Latest tax bill"
              value={latestBill && latestBill.tax_amount != null ? `$${latestBill.tax_amount.toLocaleString()}` : '—'}
              caption={latestBill ? `${billYearLabel(latestBill.tax_year, latestBill.process_year)} · ${humanizeToken(latestBill.status)}` : 'No bills on file'}
              accent="amber"
            />
          </>
        }
      />

      <Tabs
        activeValue={tab}
        onChange={(v) => setTab(v as typeof tab)}
        items={[
          { value: 'overview', label: 'Overview' },
          { value: 'map', label: 'Map' },
          { value: 'values', label: 'Values', count: values.length },
          { value: 'bills', label: 'Tax Bills', count: bills.length },
          { value: 'valuation', label: 'Valuation' },
          { value: 'contacts', label: 'Contacts' },
          { value: 'groups', label: groupTabLabel(groupCount) },
          { value: 'notes', label: 'Notes' },
          { value: 'documents', label: 'Documents' },
          { value: 'activity', label: 'Activity' },
          { value: 'custom', label: 'Custom Fields' },
          { value: 'related', label: 'Related Parcels' },
        ]}
      />

      <div style={{ marginTop: 'var(--sp-5)' }}>
        {tab === 'overview' && <OverviewTab parcel={parcel} canEdit={canEditParcel} linkedReturn={returnsQuery.data?.[0] ?? null} returnsLoading={returnsQuery.isLoading} onOpenReturn={(rid) => navigate(`/filed-returns/${rid}`)} />}
        {tab === 'values' && (
          <ValuesTab
            parcelId={parcel.id}
            values={values}
            appeals={appeals}
            reductions={reductions}
            records={records}
            loading={valuesQuery.isLoading || appealsQuery.isLoading}
            canEdit={canEditParcel}
            onAdd={() => setAddValueOpen(true)}
            // Editing a value box opens that tax-year's value card
            // (/parcel-values/:id → ParcelValueDetail, §4.3) for in-place edits.
            onEditValue={(v) => navigate(`/parcel-values/${v.id}`)}
            onOpenAppeal={(a) => navigate(`/appeals/${a.id}`)}
            onEmailAppeal={onEmailAppeal}
          />
        )}
        {tab === 'bills' && (
          <TaxesTab
            bills={bills}
            records={records}
            billsLoading={billsQuery.isLoading}
            recordsLoading={recordsQuery.isLoading}
            jurisdictionNames={jurisdictionNames}
            // Every year the values tab shows — a year with values but no bill is stated
            // rather than silently omitted.
            valueYears={valueYears}
            onBillClick={(b) => navigate(`/tax-bills/${b.id}`)}
            onRecordClick={(r) => navigate(`/jurisdictions/${r.jurisdiction_id}`)}
          />
        )}
        {tab === 'valuation' && <ParcelValuationTab parcelId={parcel.id} taxYear={parcel.year} />}
        {tab === 'contacts' && <EntityContactsPanel entityType="parcels" entityId={parcel.id} />}
        {tab === 'groups' && <EntityGroupsPanel entityType="parcel" entityId={parcel.id} />}
        {tab === 'notes' && <EntityNotesPanel entityType="parcels" entityId={parcel.id} />}
        {tab === 'documents' && (
          <div style={{ padding: 'var(--sp-5)', background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 'var(--r-lg)' }}>
            <p style={{ margin: '0 0 var(--sp-3)', fontSize: 'var(--fs-sm)', color: 'var(--color-ink-2)' }}>
              Documents related to this parcel are linked to its property.
            </p>
            <Button variant="secondary" size="sm" onClick={() => navigate(`/properties/${parcel.property_id}?tab=documents`)}>
              View property documents
            </Button>
          </div>
        )}
        {tab === 'activity' && <EntityActivityFeed entityType="parcels" entityId={parcel.id} />}
        {tab === 'custom' && <CustomFieldsPanel entityType="parcels" entityId={parcel.id} />}
        {tab === 'related' && <RelatedParcelsPanel parcelId={parcel.id} />}
        {tab === 'map' && (
          <ParcelMapTab
            propertyId={parcel.property_id}
            accountNumber={parcel.account_number}
            county={parcel.county ?? null}
          />
        )}
      </div>

      <CreateEntityModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        table="parcels"
        recordId={parcel.id}
        invalidateKeys={[['parcels-list'], ['parcel', parcel.id]]}
      />

      {emailAppeal && (
        <GenerateEmailModal
          open
          onClose={() => setEmailAppeal(null)}
          scope={{ entityType: 'appeals', entityId: emailAppeal.id, label: `${emailAppeal.tax_year ?? ''} appeal`.trim() }}
        />
      )}
      <FileAppealModal
        open={fileAppealOpen}
        onClose={() => setFileAppealOpen(false)}
        parcelId={parcel.id}
        parcelLabel={`Parcel ${parcel.account_number}`}
      />

      <RecordOutcomeModal
        open={recordOutcomeOpen}
        onClose={() => setRecordOutcomeOpen(false)}
        parcelId={parcel.id}
        parcelLabel={`Parcel ${parcel.account_number}`}
        proposedValue={parcel.proposed_market_value}
      />

      {/* Add-value dialog (respec §4.2) — the single entry point for keying a value
          AS an appeal event. Saving POSTs parcel_values (appeal_id + forum + event +
          value_source + the market/ratio/assessed grid); the existing
          value→tax cascade (core/value-to-tax-cascade) fires unchanged, so the parcel
          snapshot, appeals, Tax Records, and Tax Bills all refresh.

          parcelState resolves the Forum/Event ladder, so it reads the JURISDICTIONAL
          `state`. `parcel_state` belongs to the mailing-address block and is empty on
          most parcels, so it is a fallback only — mirroring the
          COALESCE(state, parcel_state) precedent in enriched.js. */}
      <AddValueDialog
        open={addValueOpen}
        onClose={() => setAddValueOpen(false)}
        parcelId={parcel.id}
        taxYear={parcel.year}
        parcelState={parcel.state ?? parcel.parcel_state}
        appeals={appeals as AddValueAppealOption[]}
        canCreateAppeal={canProcessAppeal}
        // The newest record on the current year seeds the grid — adding a value is adding
        // the NEXT STAGE, so the figures carry forward and only what moved gets re-keyed.
        priorValue={priorValueForNewStage}
        invalidateKeys={[
          ['parcel-values', parcel.id],
          ['appeals-by-parcel', parcel.id],
          ['parcel', parcel.id],
          ['tax-records-by-parcel', parcel.id],
          ['tax-bills-by-parcel', parcel.id],
        ]}
      />

    </>
  );
}

function OverviewTab({ parcel, canEdit, linkedReturn, returnsLoading, onOpenReturn }: {
  parcel: Parcel;
  canEdit: boolean;
  linkedReturn: LinkedReturn | null;
  returnsLoading: boolean;
  onOpenReturn: (returnId: string) => void;
}) {
  const attrQuery = useQuery<AttrMap>({
    queryKey: ['field-attribution', 'parcels', parcel.id],
    queryFn: ({ signal }) => api.get<AttrMap>(`/api/field-attribution/parcels/${parcel.id}`, signal),
  });
  const attr = attrQuery.data ?? {};
  const record = parcel as unknown as Record<string, unknown>;
  const invalidate: unknown[][] = [['parcel', parcel.id], ['parcels-list'], ['field-attribution', 'parcels', parcel.id]];
  const classifications = useQuery<string[] | { values: string[] }>({ queryKey: ['org-settings', 'property-classifications'], queryFn: ({ signal }) => api.get('/api/org-settings/property-classifications', signal), staleTime: 5 * 60 * 1000 });

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
      gap: 'var(--sp-4)',
    }}>
      <InlineEditCard
        title="Identity" routePath="parcels" recordId={parcel.id} record={record} canEdit={canEdit} invalidateKeys={invalidate}
        attribution={latestAttr(attr, ['account_number', 'alternate_id', 're_pp', 'year'])}
        fields={[
          { key: 'account_number', label: 'Account #', required: true, display: (v) => <span style={{ fontFamily: 'var(--font-mono)' }}>{String(v ?? '—')}</span> },
          { key: 'alternate_id', label: 'Alternate ID' },
          { key: 're_pp', label: 'Type', type: 'select', options: registryOptions(classifications.data), addOptionSlug: 'property-classifications', display: (v) => (v ? humanizeToken(String(v)) : '—') },
          { key: 'year', label: 'Tax year', type: 'number' },
        ]}
      />
      <InlineEditCard
        title="Owner & Location" routePath="parcels" recordId={parcel.id} record={record} canEdit={canEdit} invalidateKeys={invalidate}
        attribution={latestAttr(attr, ['owner_name', 'address_line1', 'parcel_city', 'parcel_state', 'parcel_postal_code'])}
        fields={[
          { key: 'owner_name', label: 'Owner' },
          { key: 'address_line1', label: 'Address' },
          { key: 'parcel_city', label: 'City' },
          // A9 — State + ZIP on separate lines; State a dropdown; ZIP number format.
          { key: 'parcel_state', label: 'State', type: 'select', options: STATE_SELECT_OPTIONS.map((o) => ({ value: String(o.value), label: o.label })) },
          { key: 'parcel_postal_code', label: 'ZIP', type: 'number' },
        ]}
      />
      <InlineEditCard
        title="Appeal status" routePath="parcels" recordId={parcel.id} record={record} canEdit={canEdit} invalidateKeys={invalidate}
        attribution={latestAttr(attr, ['under_appeal', 'appeal_date', 'tracking_number', 'legal_description'])}
        fields={[
          // A9 — under_appeal is a simple Yes/No; appeal_date a date; tracking # a
          // number; legal description a LARGE textarea (these run multiple paragraphs).
          { key: 'under_appeal', label: 'Under appeal', type: 'checkbox' },
          { key: 'appeal_date', label: 'Appeal date', type: 'date', display: (v) => formatDate(v as string | null) },
          { key: 'tracking_number', label: 'Tracking #', type: 'number' },
          { key: 'legal_description', label: 'Legal description', type: 'textarea' },
        ]}
      />
      {/* Assignment model (Yadi 2026-09-16): Consultant is an internal user or a client
          contact, per parcel; Manager and Assistant are the client team, inheritable and
          overridable here. Full names only, typeahead pickers, two nullable FKs. */}
      {parcel.re_pp === 'personal_property' && (
        <BppReturnCard linkedReturn={linkedReturn} loading={returnsLoading} onOpen={onOpenReturn} />
      )}
      <ExemptionsCard parcel={parcel} canEdit={canEdit} />
    </div>
  );
}

/**
 * BppReturnCard — §6.2. A PP parcel is a BPP account: surface its return log
 * entry right on the parcel profile (status + deadline + reported value) with a
 * one-click link into the return, so the parcel ↔ return relationship is visible
 * from the parcel without a trip to the Returns list. When no return exists yet
 * it says so (the auto-create rule attaches one when the client has the returns
 * service; otherwise a return is created from the Returns list).
 */
function BppReturnCard({ linkedReturn, loading, onOpen }: { linkedReturn: LinkedReturn | null; loading: boolean; onOpen: (returnId: string) => void }) {
  return (
    <InfoCard label="BPP Return">
      {loading ? (
        <p style={{ margin: 0, fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>Loading return…</p>
      ) : linkedReturn ? (
        <>
          <Row label="Status" value={<Badge variant="neutral">{linkedReturn.status.replace(/_/g, ' ')}</Badge>} />
          <Row label="Tax year" value={String(linkedReturn.tax_year)} />
          <Row label="Rendition deadline" value={formatDate(linkedReturn.due_date)} />
          <Row label="Reported value" value={linkedReturn.reported_amount != null ? `$${linkedReturn.reported_amount.toLocaleString()}` : '—'} />
          <div style={{ marginTop: 'var(--sp-3)' }}>
            <Button variant="secondary" size="sm" onClick={() => onOpen(linkedReturn.id)}>Open return ↗</Button>
          </div>
        </>
      ) : (
        <p style={{ margin: 0, fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>
          No return on file for this PP account yet.
        </p>
      )}
    </InfoCard>
  );
}

/** Bool-ish (0/1/true/false/null) → strict boolean. */
function isOn(v: boolean | number | null | undefined): boolean {
  return v === true || v === 1;
}

/**
 * ExemptionsCard — one-click exemption checkboxes wired to
 * PUT /api/parcels/:id/exemptions. Each toggle is an immediate single-flag write;
 * the parcel query is invalidated on success so the tax cascade + flag state
 * re-render from the API.
 *
 * ⚠️ There used to be a third checkbox, "Owner occupied", and it GATED these two —
 * both disabled until it was on. Yadira had the field removed (Y10, confirmed in
 * words 2026-08-05, migration 146) because homestead already means owner-occupancy,
 * so the flag and its gate were duplicative. Homestead and over-65 are now set
 * directly. Do not re-add an eligibility gate here without a stated source.
 */
function ExemptionsCard({ parcel, canEdit }: { parcel: Parcel; canEdit: boolean }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const homestead = isOn(parcel.homestead);
  const over65 = isOn(parcel.over_65);

  const mutation = useMutation<Parcel, Error, Partial<Record<'homestead' | 'over_65', boolean>>>({
    mutationFn: (flags) => api.put<Parcel>(`/api/parcels/${parcel.id}/exemptions`, flags),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parcel', parcel.id] });
      queryClient.invalidateQueries({ queryKey: ['tax-records-by-parcel', parcel.id] });
      queryClient.invalidateQueries({ queryKey: ['tax-bills-by-parcel', parcel.id] });
    },
    onError: (err) => {
      const msg = err instanceof ApiClientError && err.body && typeof err.body === 'object' && 'error' in err.body
        ? String((err.body as { error: unknown }).error)
        : err.message;
      toast({ variant: 'error', message: msg });
    },
  });

  const set = (flag: 'homestead' | 'over_65', value: boolean) =>
    mutation.mutate({ [flag]: value });

  const busy = mutation.isPending;
  const disabled = !canEdit || busy;

  return (
    <InfoCard label="Exemptions">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)', paddingTop: 'var(--sp-1)' }}>
        <Checkbox
          label="Homestead"
          description="FS-1 residential homestead exemption."
          checked={homestead}
          disabled={disabled}
          onChange={(e) => set('homestead', e.target.checked)}
        />
        <Checkbox
          label="Over 65"
          description="Stacks on top of the homestead exemption."
          checked={over65}
          disabled={disabled}
          onChange={(e) => set('over_65', e.target.checked)}
        />
        {!canEdit && (
          <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>
            You don't have permission to change exemptions.
          </span>
        )}
      </div>
    </InfoCard>
  );
}

/**
 * The order in which a year's valuations progress as an appeal moves along —
 * the proposed notice → informal/formal hearing → arbitration/litigation →
 * settled/final record. Used to sequence a year's value rows so the tab reads
 * as the appeal's progression, not an arbitrary list. Unknown types sort last.
 */
const VALUE_TYPE_PROGRESSION = [
  'proposed', 'informal_hearing', 'formal_hearing', 'arbitration', 'litigation',
  'supplemental', 'corrected', 'target', 'budget', 'final', 'other',
];
function valueTypeRank(t: string): number {
  const i = VALUE_TYPE_PROGRESSION.indexOf(t);
  return i === -1 ? VALUE_TYPE_PROGRESSION.length : i;
}

/** Parse an appeal's covered_tax_years (TEXT JSON array) → number[]; [] when
 *  unset/malformed. A covered appeal fans onto every covered year's card. */
function coveredYearsOf(a: Appeal): number[] {
  if (!a.covered_tax_years) return [];
  try {
    const parsed = JSON.parse(a.covered_tax_years);
    return Array.isArray(parsed) ? parsed.map(Number).filter((n) => Number.isInteger(n)) : [];
  } catch {
    return [];
  }
}

/** Sort value boxes along the appeal progression: by date when present, else by
 *  the value-type progression rank (READ-SAFE — legacy rows have no date). */
function orderBoxes(vals: ParcelValue[]): ParcelValue[] {
  return [...vals].sort((a, b) => {
    if (a.date && b.date) return a.date.localeCompare(b.date);
    if (a.date) return -1;
    if (b.date) return 1;
    return valueTypeRank(a.value_type) - valueTypeRank(b.value_type);
  });
}

/** One appeal (or the unlinked-legacy fallback) with its value boxes for a year. */
interface AppealTrackModel {
  key: string;
  appeal: Appeal | null;
  boxes: ParcelValue[];
  /** True when the appeal owns a DIFFERENT year and fans onto this one (299(c)). */
  carried: boolean;
}

/** One tax-year card: its appeal tracks, reductions, report source + realized savings. */
interface YearCardModel {
  year: number;
  tracks: AppealTrackModel[];
  reductions: Reduction[];
  reportSourceAppeal: Appeal | null;
  appealCount: number;
  /** Realized only (a final value exists): assessed reduced + tax saved; null = no final. */
  valueReduced: number | null;
  taxSaved: number | null;
}

/**
 * ValuesTab (appeal-lifecycle respec §4.1) — per-YEAR cards. Each card carries a
 * TRACK per appeal and a TRACK per reduction, replacing the old stacked
 * "appeal progress timeline" + "value history by year". An appeal track shows its
 * values (grouped by appeal_id) as collapsed, expandable boxes; a reduction track
 * shows a requested→granted ladder. The footer reports REALIZED savings only.
 *
 * READ-SAFE: legacy rows have NULL forum/event/appeal_id — a box falls back to a
 * value-only display, and a year with no appeal renders a value-only track plus a
 * "no appeal filed yet" note.
 */
function ValuesTab({ parcelId, values, appeals, reductions, records, loading, canEdit, onAdd, onEditValue, onOpenAppeal, onEmailAppeal }: {
  parcelId: string;
  values: ParcelValue[]; appeals: Appeal[]; reductions: Reduction[]; records: TaxRecord[];
  loading: boolean; canEdit: boolean;
  onAdd: () => void; onEditValue: (v: ParcelValue) => void; onOpenAppeal: (a: Appeal) => void; onEmailAppeal: (a: Appeal) => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // ATA part 3 §5a: the savings baseline is no longer movable from the screen. Invoice creation is
  // driven by the appeal stage and the detected savings, and a voided draft moves the baseline itself.
  // §5 — INLINE EDITING. A value is edited on the card itself; routing to
  // /parcel-values/:id stays available for what only lives there (exemptions,
  // phase-in, classification).
  //
  // ⚠️ Only the COMPONENTS are writable here. `market_total` and `assessed_total`
  // are DERIVED (business-rules.computeValueTotals sums land + improvement +
  // personal), so a total written directly is recomputed on the spot — no error,
  // no log, just a wrong figure under a success banner. Writing the engine's
  // inputs and letting it recompute is the only way this stays honest, which is
  // why the editor shows the totals read-only.
  const saveValue = useMutation<unknown, Error, { valueId: string; patch: Record<string, number | string | null> }>({
    mutationFn: ({ valueId, patch }) => api.put(`/api/parcel-values/${valueId}`, patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parcel-values', parcelId] });
      queryClient.invalidateQueries({ queryKey: ['parcel', parcelId] });
      // The totals feed the bill, so the derived surfaces have to re-read too.
      queryClient.invalidateQueries({ queryKey: ['tax-records-by-parcel', parcelId] });
      queryClient.invalidateQueries({ queryKey: ['tax-bills-by-parcel', parcelId] });
      toast({ variant: 'success', message: 'Value updated.' });
    },
    onError: (err) => toast({
      variant: 'error',
      message: err instanceof ApiClientError ? err.message : 'Could not save the value.',
    }),
  });

  // The effective tax rate per year, derived from the actual bill:
  // rate = Σ(records.tax_amount for the year) ÷ current assessed for the year.
  // Used to turn a realized assessed reduction into dollars of tax saved.
  const rateByYear = useMemo(() => {
    const taxByYear = new Map<number, number>();
    for (const r of records) if (r.tax_amount != null) taxByYear.set(r.tax_year, (taxByYear.get(r.tax_year) ?? 0) + r.tax_amount);
    const assessedByYear = new Map<number, number>();
    for (const v of values) {
      if (isOn(v.is_current_value) && v.assessed_total != null) assessedByYear.set(v.tax_year, v.assessed_total);
    }
    const out = new Map<number, number>();
    for (const [year, tax] of taxByYear) {
      const assessed = assessedByYear.get(year);
      if (assessed && assessed > 0) out.set(year, tax / assessed);
    }
    return out;
  }, [records, values]);

  const cards = useMemo<YearCardModel[]>(() => {
    // Every year that has a value, an appeal (own or covered), or a reduction.
    const yearSet = new Set<number>();
    for (const v of values) yearSet.add(v.tax_year);
    for (const a of appeals) {
      if (a.tax_year != null) yearSet.add(a.tax_year);
      for (const y of coveredYearsOf(a)) yearSet.add(y);
    }
    for (const r of reductions) if (r.tax_year != null) yearSet.add(r.tax_year);
    const appealById = new Map(appeals.map((a) => [a.id, a] as const));

    return [...yearSet].sort((a, b) => b - a).map((year) => {
      const yearVals = values.filter((v) => v.tax_year === year);
      const yearAppeals = appeals.filter((a) => a.tax_year === year || coveredYearsOf(a).includes(year));
      const ownAppeal = yearAppeals.find((a) => a.tax_year === year) ?? null;

      // Group the year's values by their appeal_id; NULL appeal_id = unlinked legacy.
      const byAppeal = new Map<string, ParcelValue[]>();
      const unlinked: ParcelValue[] = [];
      for (const v of yearVals) {
        if (v.appeal_id) { const arr = byAppeal.get(v.appeal_id) ?? []; arr.push(v); byAppeal.set(v.appeal_id, arr); }
        else unlinked.push(v);
      }

      const tracks: AppealTrackModel[] = [];
      // A track per appeal on this year. The own-year appeal absorbs unlinked
      // legacy values (they belong to this year's appeal even without the link).
      for (const a of yearAppeals) {
        const boxes = [...(byAppeal.get(a.id) ?? [])];
        byAppeal.delete(a.id);
        if (a === ownAppeal) boxes.push(...unlinked);
        tracks.push({ key: a.id, appeal: a, boxes: orderBoxes(boxes), carried: a.tax_year !== year });
      }
      // Any value linked to an appeal not surfaced above (defensive) → its own track.
      for (const [appealId, boxes] of byAppeal) {
        tracks.push({ key: appealId, appeal: appealById.get(appealId) ?? null, boxes: orderBoxes(boxes), carried: false });
      }
      // No own-year appeal but there ARE unlinked values → a value-only track.
      if (!ownAppeal && unlinked.length) tracks.push({ key: `unlinked-${year}`, appeal: null, boxes: orderBoxes(unlinked), carried: false });

      // Realized savings: baseline value − final value, only when a final exists.
      const baseline = yearVals.find((v) => isOn(v.is_beginning_value)) ?? null;
      const finalVal = yearVals.find((v) => v.value_type === 'final') ?? null;
      const valueReduced = baseline && finalVal && baseline.assessed_total != null && finalVal.assessed_total != null
        ? Math.max(0, baseline.assessed_total - finalVal.assessed_total)
        : null;
      const rate = rateByYear.get(year);
      const taxSaved = valueReduced != null && rate != null ? valueReduced * rate : null;

      return {
        year,
        tracks,
        reductions: reductions.filter((r) => r.tax_year === year),
        reportSourceAppeal: ownAppeal ?? yearAppeals[0] ?? null,
        appealCount: yearAppeals.length,
        valueReduced,
        taxSaved,
      };
    });
  }, [values, appeals, reductions, rateByYear]);

  return (
    <SectionPanel
      title="Values & appeal history"
      description="One card per tax year — each appeal and each exemption runs as its own track. A value box shows forum · event · date on its face; open it for the land / improvement / ratio / assessed detail."
      canEdit={canEdit}
      addLabel="Add value"
      onAdd={onAdd}
    >
      {appeals.length === 0 && (
        <div style={{
          padding: 'var(--sp-4)', background: 'var(--color-card)', border: '1px dashed var(--color-border)',
          borderRadius: 'var(--r-lg)', color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)',
        }}>
          No appeal filed yet for this parcel. The year cards below reflect assessment activity; once an appeal is filed its values track here.
        </div>
      )}
      {loading ? (
        <p style={{ color: 'var(--color-ink-3)', margin: 0 }}>Loading values…</p>
      ) : cards.length === 0 ? (
        <Empty message="No parcel values recorded yet." />
      ) : (
        cards.map((card) => (
          <YearCard
            key={card.year}
            card={card}
            canEdit={canEdit}
            onEditValue={onEditValue}
            onSaveValue={(valueId, patch) => saveValue.mutateAsync({ valueId, patch })}
            savePending={saveValue.isPending}
            onOpenAppeal={onOpenAppeal}
            onEmailAppeal={onEmailAppeal}
          />
        ))
      )}
    </SectionPanel>
  );
}

/** Derived "reports & invoices run from" label for a year's appeal diamond. */
function reportSourceLabel(a: Appeal | null, year: number): string {
  if (!a) return '— (no appeal yet)';
  if (a.tax_year != null && a.tax_year !== year) return `Carried ${a.tax_year} appeal`;
  return `${a.tax_year ?? year} appeal`;
}

/**
 * YearCard — one tax year: header (year · counts · report source), an appeal
 * track per appeal, a reduction track per reduction, and a realized-savings
 * footer. A covered (299(c)) appeal appears on every year it covers.
 */
function YearCard({ card, canEdit, onEditValue, onSaveValue, savePending, onOpenAppeal, onEmailAppeal }: {
  card: YearCardModel; canEdit: boolean;
  onEditValue: (v: ParcelValue) => void; onOpenAppeal: (a: Appeal) => void; onEmailAppeal: (a: Appeal) => void;
  onSaveValue: (valueId: string, patch: Record<string, number | string | null>) => Promise<unknown>;
  savePending: boolean;
}) {
  return (
    <div style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 'var(--r-lg)', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--sp-3)', flexWrap: 'wrap',
        padding: 'var(--sp-3) var(--sp-4)', borderBottom: '1px solid var(--color-border)',
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
            <h4 style={{ margin: 0, fontSize: 'var(--fs-md)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>Tax year {card.year}</h4>
            <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
              {card.appealCount} appeal{card.appealCount === 1 ? '' : 's'} · {card.reductions.length} exemption{card.reductions.length === 1 ? '' : 's'}
            </span>
          </div>
          <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
            Reports and invoices run from: <span style={{ color: 'var(--color-ink)', fontWeight: 'var(--fw-medium)' }}>{reportSourceLabel(card.reportSourceAppeal, card.year)}</span>
          </span>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)', padding: 'var(--sp-4)' }}>
        {card.tracks.map((track) => (
          <AppealTrackView
            key={track.key}
            track={track}
            year={card.year}
            canEdit={canEdit}
            onOpenAppeal={onOpenAppeal}
            onEmailAppeal={onEmailAppeal}
            onEditValue={onEditValue}
            onSaveValue={onSaveValue}
            savePending={savePending}
          />
        ))}
        {card.reductions.map((r) => <ExemptionTrackView key={r.id} reduction={r} />)}
      </div>

      {/* Footer — realized savings only (no final → "no final yet"). */}
      <div style={{
        padding: 'var(--sp-3) var(--sp-4)', borderTop: '1px solid var(--color-border)',
        background: 'var(--color-bg)', fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)',
      }}>
        {card.valueReduced != null ? (
          <span>
            Value reduced <span style={{ color: 'var(--color-status-green)', fontWeight: 'var(--fw-medium)' }}>${card.valueReduced.toLocaleString()}</span> assessed
            {card.taxSaved != null && <> · Tax saved <span style={{ color: 'var(--color-status-green)', fontWeight: 'var(--fw-medium)' }}>${Math.round(card.taxSaved).toLocaleString()}</span></>}
          </span>
        ) : (
          <span>No final yet — savings realize once the appeal settles.</span>
        )}
      </div>
    </div>
  );
}

/**
 * AppealTrackView — one appeal's value boxes for a year, in a scoped
 * horizontally-scrolling row (stacked vertically on compact viewports). The head
 * labels the appeal (stage badge + a carried/covers note) and opens the appeal.
 */
function AppealTrackView({ track, year, canEdit, onOpenAppeal, onEmailAppeal, onEditValue, onSaveValue, savePending }: {
  track: AppealTrackModel; year: number; canEdit: boolean;
  onOpenAppeal: (a: Appeal) => void; onEmailAppeal: (a: Appeal) => void; onEditValue: (v: ParcelValue) => void;
  onSaveValue: (valueId: string, patch: Record<string, number | string | null>) => Promise<unknown>;
  savePending: boolean;
}) {
  const compact = useIsCompact();
  const a = track.appeal;
  const covered = a ? coveredYearsOf(a) : [];
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', flexWrap: 'wrap', marginBottom: 'var(--sp-2)' }}>
        <span style={{ color: 'var(--brand-primary)' }}>◆</span>
        <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>
          {a ? (a.tax_year != null ? `${a.tax_year} appeal` : 'Appeal') : 'Values (no appeal)'}
        </span>
        {a && <Badge variant={stageVariant(a.lifecycle_stage)}>{stageLabel(a.lifecycle_stage)}</Badge>}
        {track.carried && a?.tax_year != null && <Badge variant="info">Carried from {a.tax_year}</Badge>}
        {!track.carried && covered.length > 1 && <Badge variant="info">Covers {covered[0]}–{covered[covered.length - 1]}</Badge>}
        {a && <Button variant="secondary" size="sm" onClick={() => onOpenAppeal(a)}>Open appeal ↗</Button>}
        {/* §7 item 27: the stage's email templates, picked right here on the lifecycle. */}
        {a && <Button variant="secondary" size="sm" onClick={() => onEmailAppeal(a)}>Email template</Button>}
      </div>
      {track.boxes.length === 0 ? (
        <p style={{ margin: 0, fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>No value keyed for this appeal yet.</p>
      ) : (
        <div style={{
          display: 'flex', flexDirection: compact ? 'column' : 'row',
          gap: 'var(--sp-3)', overflowX: compact ? 'visible' : 'auto', paddingBottom: compact ? 0 : 'var(--sp-2)',
        }}>
          {track.boxes.map((box) => (
            <ValueBox
              key={box.id}
              box={box}
              year={year}
              canEdit={canEdit}
              onEdit={() => onEditValue(box)}
              onSaveValue={onSaveValue}
              savePending={savePending}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** The value box's face label — shared with the value's own detail page so the two
 *  screens can never name the same row differently (see lib/valueLabel). */
const boxFaceLabel = (box: ParcelValue): string => valueFaceLabel(box);

/**
 * ValueBox — one appeal-event value, collapsed by default. Face: forum · event ·
 * date · market total · taxable. Open (caret or the face): land / improvement /
 * ratio / assessed / exempt.
 *
 * Two controls (§5.4). The diamond only MARKS the appeal's savings baseline (◆) — since ATA
 * part 3 §5a it is not movable from here; a voided draft moves it. The pencil edits the value
 * IN PLACE (§5). The caret expands. Expanding is the safe action, so it owns the large
 * surface: a near-miss reveals detail rather than re-basing a fee.
 *
 * §5 — the pencil used to navigate to /parcel-values/:id. It now opens an inline
 * editor on the card; the full record stays one click away for what only lives
 * there (exemptions, phase-in/299(c), classification).
 */
function ValueBox({ box, year, canEdit, onEdit, onSaveValue, savePending }: {
  box: ParcelValue; year: number; canEdit: boolean;
  onEdit: () => void;
  onSaveValue: (valueId: string, patch: Record<string, number | string | null>) => Promise<unknown>;
  savePending: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const isBaseline = isOn(box.is_beginning_value);
  // Taxable = assessed base (exemptions live on the value's own card; shown "—" here).
  const taxable = box.assessed_total;
  const blended = box.market_total && box.assessed_total ? box.assessed_total / box.market_total : null;
  const dateLabel = box.date ? formatDate(box.date.slice(0, 10)) : '—';

  return (
    <div style={{
      minWidth: 220, flex: '0 0 auto',
      border: `1px solid ${isBaseline ? 'var(--brand-primary)' : 'var(--color-border)'}`,
      borderRadius: 'var(--r-md)', background: 'var(--color-card)',
      boxShadow: isBaseline ? '0 0 0 3px var(--brand-primary-bg)' : 'none',
    }}>
      {/* Face — expands. Expanding must never move a billed number (§5.4), so the
          large default surface does the SAFE thing and the savings baseline lives on
          its own deliberate control (the diamond). Previously the whole face wrote the
          baseline while a ~10px caret expanded, so a near-miss silently re-based a fee.
          The face is a plain div, not a role=button, so the two real buttons inside it
          stay reachable; keyboard users expand via the caret. */}
      <div
        onClick={() => setOpen((o) => !o)}
        style={{ padding: 'var(--sp-3)', cursor: 'pointer' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--sp-2)' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-1)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)', color: 'var(--color-ink)' }}>
            <span title={isBaseline ? 'Savings baseline — savings are measured from here' : undefined} style={{ color: isBaseline ? 'var(--brand-primary)' : 'var(--color-border-strong)' }}>{isBaseline ? '◆' : '◇'}</span>
            {boxFaceLabel(box)}
          </span>
          <span style={{ display: 'flex', gap: 4 }}>
            {canEdit && (
              <button
                type="button"
                aria-label={editing ? 'Cancel editing value' : 'Edit value'}
                title={editing ? 'Cancel editing' : 'Edit value here'}
                onClick={(e) => {
                  e.stopPropagation();
                  setEditing((v) => { const next = !v; if (next) setOpen(true); return next; });
                }}
                style={{
                  border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--brand-primary)',
                  width: 22, height: 22, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0,
                }}
              ><IconPencil size={13} /></button>
            )}
            <button
              type="button"
              aria-label={open ? 'Collapse value detail' : 'Expand value detail'}
              title={open ? 'Collapse value detail' : 'Expand value detail'}
              aria-expanded={open}
              onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
              style={{
                border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--color-ink-3)',
                width: 22, height: 22, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                padding: 0, fontSize: 'var(--fs-sm)',
              }}
            >{open ? '▾' : '▸'}</button>
          </span>
        </div>
        <div style={{ marginTop: 'var(--sp-1)', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>{dateLabel}</div>
        <div style={{ marginTop: 'var(--sp-2)', display: 'flex', gap: 'var(--sp-3)' }}>
          <BoxStat label="Market" value={box.market_total} />
          <BoxStat label="Taxable" value={taxable} />
        </div>
      </div>
      {/* Expanded detail — the inline editor when editing, else the read-out. */}
      {open && editing && (
        <ValueBoxEditor
          box={box}
          savePending={savePending}
          onCancel={() => setEditing(false)}
          onOpenFullRecord={onEdit}
          onSave={async (patch) => { await onSaveValue(box.id, patch); setEditing(false); }}
        />
      )}
      {open && !editing && (
        <div style={{ padding: 'var(--sp-3)', borderTop: '1px solid var(--color-border)', display: 'flex', flexDirection: 'column', gap: 2 }}>
          <BoxRow label="Land (market)" value={box.market_land} />
          <BoxRow label="Improvement (market)" value={box.market_imp} />
          <BoxRow label="Ratio (blended)" value={blended != null ? blended.toFixed(4) : '—'} raw />
          <BoxRow label="Assessed" value={box.assessed_total} />
          <BoxRow label="Exempt" value="—" raw />
          {/* §2.8 — the alternate value is LABELLED BY ITS TYPE, matching how the
              operator's previous system names the row. Shown only when one is set:
              an "Alternate value —" on every box would be noise on the vast
              majority of values that legitimately have none. */}
          {box.alternate_value != null && (
            <BoxRow label={box.alternate_value_type || 'Alternate value'} value={box.alternate_value} />
          )}
          {box.value_source && <BoxRow label="Source" value={box.value_source} raw />}
          {isOn(box.is_current_value) && <div style={{ marginTop: 4 }}><Badge variant="success" dot>Current value ({year})</Badge></div>}
        </div>
      )}
    </div>
  );
}

/**
 * ValueBoxEditor — §5 inline editing. Edits a value on the Values tab instead of
 * routing to /parcel-values/:id.
 *
 * ⚠️ COMPONENTS ONLY. `market_total` and `assessed_total` are DERIVED
 * (business-rules.computeValueTotals sums land + improvement + personal). A total
 * written straight to the API is recomputed on the spot — no error, no log, just a
 * wrong number under a success banner. So the totals render read-only, live, from
 * whatever the components currently say, and only the components are sent.
 *
 * An untouched field is omitted from the patch entirely rather than sent as its
 * current value: a no-op write still lands in the audit trail as an edit, and a
 * blanked field must be able to mean NULL without every other field claiming to
 * have been re-keyed at the same moment.
 */
const EDITABLE_COMPONENTS = [
  { key: 'market_land', label: 'Land (market)' },
  { key: 'market_imp', label: 'Improvement (market)' },
  { key: 'market_pp', label: 'Personal property (market)' },
  { key: 'assessed_land', label: 'Land (assessed)' },
  { key: 'assessed_imp', label: 'Improvement (assessed)' },
  { key: 'assessed_pp', label: 'Personal property (assessed)' },
] as const;

function ValueBoxEditor({ box, savePending, onCancel, onSave, onOpenFullRecord }: {
  box: ParcelValue;
  savePending: boolean;
  onCancel: () => void;
  onSave: (patch: Record<string, number | string | null>) => void | Promise<void>;
  onOpenFullRecord: () => void;
}) {
  const initial = useMemo(() => {
    const o: Record<string, string> = {};
    for (const f of EDITABLE_COMPONENTS) {
      const v = box[f.key] as number | null | undefined;
      o[f.key] = v == null ? '' : String(v);
    }
    o.date = box.date ? box.date.slice(0, 10) : '';
    return o;
  }, [box]);
  const [draft, setDraft] = useState<Record<string, string>>(initial);

  const num = (v: string): number | null => {
    const t = v.trim();
    if (t === '') return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
  };
  // The derived totals, recomputed live from the draft so the operator sees what
  // the engine will store rather than a stale figure from before the edit.
  const sum = (keys: readonly string[]) => {
    const parts = keys.map((k) => num(draft[k] ?? ''));
    return parts.every((x) => x == null) ? null : parts.reduce((a: number, b) => a + (b ?? 0), 0);
  };
  const marketTotal = sum(['market_land', 'market_imp', 'market_pp']);
  const assessedTotal = sum(['assessed_land', 'assessed_imp', 'assessed_pp']);

  const invalid = EDITABLE_COMPONENTS.some((f) => {
    const t = (draft[f.key] ?? '').trim();
    return t !== '' && !Number.isFinite(Number(t));
  });

  function submit() {
    const patch: Record<string, number | string | null> = {};
    for (const f of EDITABLE_COMPONENTS) {
      if ((draft[f.key] ?? '') !== (initial[f.key] ?? '')) patch[f.key] = num(draft[f.key] ?? '');
    }
    if ((draft.date ?? '') !== (initial.date ?? '')) patch.date = draft.date.trim() === '' ? null : draft.date;
    if (Object.keys(patch).length === 0) { onCancel(); return; }
    onSave(patch);
  }

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{ padding: 'var(--sp-3)', borderTop: '1px solid var(--color-border)', display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}
    >
      {EDITABLE_COMPONENTS.map((f) => (
        <label key={f.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--sp-2)', fontSize: 'var(--fs-xs)' }}>
          <span style={{ color: 'var(--color-ink-3)' }}>{f.label}</span>
          <input
            type="number"
            inputMode="decimal"
            aria-label={f.label}
            value={draft[f.key] ?? ''}
            onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
            style={{
              width: 120, padding: '2px var(--sp-1)', textAlign: 'right',
              border: '1px solid var(--color-border)', borderRadius: 'var(--r-sm)',
              background: 'var(--color-bg)', color: 'var(--color-ink)', fontSize: 'var(--fs-xs)',
            }}
          />
        </label>
      ))}
      <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--sp-2)', fontSize: 'var(--fs-xs)' }}>
        <span style={{ color: 'var(--color-ink-3)' }}>As-of date</span>
        <input
          type="date"
          aria-label="As-of date"
          value={draft.date ?? ''}
          onChange={(e) => setDraft((d) => ({ ...d, date: e.target.value }))}
          style={{
            width: 120, padding: '2px var(--sp-1)',
            border: '1px solid var(--color-border)', borderRadius: 'var(--r-sm)',
            background: 'var(--color-bg)', color: 'var(--color-ink)', fontSize: 'var(--fs-xs)',
          }}
        />
      </label>

      {/* Derived, never keyed — see the note above. */}
      <div style={{ borderTop: '1px dashed var(--color-border)', paddingTop: 'var(--sp-2)', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <BoxRow label="Market total (derived)" value={marketTotal} />
        <BoxRow label="Assessed total (derived)" value={assessedTotal} />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', flexWrap: 'wrap', marginTop: 'var(--sp-1)' }}>
        <Button variant="primary" size="sm" disabled={savePending || invalid} onClick={submit}>
          {savePending ? 'Saving…' : 'Save'}
        </Button>
        <Button variant="secondary" size="sm" disabled={savePending} onClick={onCancel}>Cancel</Button>
        <button
          type="button"
          onClick={onOpenFullRecord}
          style={{ marginLeft: 'auto', border: 'none', background: 'none', padding: 0, cursor: 'pointer', color: 'var(--brand-primary)', fontSize: 'var(--fs-xs)' }}
        >Open full record ↗</button>
      </div>
      {invalid && (
        <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-status-red, red)' }}>Enter numbers only.</span>
      )}
    </div>
  );
}

function BoxStat({ label, value }: { label: string; value: number | null | undefined }) {
  return (
    <span>
      <span style={{ display: 'block', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>{label}</span>
      <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink)', fontWeight: 'var(--fw-medium)' }}>{value != null ? `$${value.toLocaleString()}` : '—'}</span>
    </span>
  );
}

function BoxRow({ label, value, raw }: { label: string; value: number | string | null | undefined; raw?: boolean }) {
  const content = raw ? (value ?? '—') : (typeof value === 'number' ? `$${value.toLocaleString()}` : '—');
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--sp-2)', fontSize: 'var(--fs-xs)' }}>
      <span style={{ color: 'var(--color-ink-3)' }}>{label}</span>
      <span style={{ color: 'var(--color-ink)' }}>{content}</span>
    </div>
  );
}

/**
 * ExemptionTrackView — an exemption as a short requested→granted ladder carrying
 * an amount (§4.1). Denied exemptions read as a terminal step. Exemptions bill on
 * their own flags, so they are never the report/invoice source diamond.
 *
 * §1.1 — the row's fallback label is the object's name, never "Reduction": that
 * word names the BILLING basis (reduction contingency), and printing it on a
 * record that changes taxable value is the collision the rename closes.
 */
function ExemptionTrackView({ reduction }: { reduction: Reduction }) {
  const status = (reduction.status ?? '').toLowerCase();
  const amount = reduction.amount;
  const amountLabel = amount != null ? `$${amount.toLocaleString()}` : (reduction.percentage != null ? `${reduction.percentage}%` : '—');
  // Ladder: Requested → Granted (or → Denied). The reached step is highlighted.
  const steps = status === 'denied'
    ? [{ key: 'requested', label: 'Requested' }, { key: 'denied', label: 'Denied' }]
    : [{ key: 'requested', label: 'Requested' }, { key: 'granted', label: 'Granted' }];
  const reachedIndex = status === 'granted' || status === 'denied' ? 1 : 0;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', flexWrap: 'wrap', marginBottom: 'var(--sp-2)' }}>
        <span style={{ color: 'var(--color-ink-3)' }}>◇</span>
        <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>
          {reduction.type ? reduction.type.replace(/_/g, ' ') : 'Exemption'}
        </span>
        {reduction.status && <Badge variant={status === 'denied' ? 'danger' : status === 'granted' ? 'success' : 'neutral'}>{reduction.status.replace(/_/g, ' ')}</Badge>}
        <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>Amount <span style={{ color: 'var(--color-ink)', fontWeight: 'var(--fw-medium)' }}>{amountLabel}</span></span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
        {steps.map((s, i) => (
          <span key={s.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
            <span style={{
              padding: '2px var(--sp-2)', borderRadius: 'var(--r-sm)', fontSize: 'var(--fs-xs)',
              border: `1px solid ${i <= reachedIndex ? 'var(--brand-primary)' : 'var(--color-border)'}`,
              background: i <= reachedIndex ? 'var(--brand-primary-bg)' : 'transparent',
              color: i <= reachedIndex ? 'var(--brand-primary)' : 'var(--color-ink-3)',
              fontWeight: 'var(--fw-medium)',
            }}>{s.label}</span>
            {i < steps.length - 1 && <span style={{ color: 'var(--color-ink-3)' }}>→</span>}
          </span>
        ))}
      </div>
    </div>
  );
}

/**
 * SectionPanel — a titled panel with an optional "+ Add" action, used by the
 * in-profile Values tab so entry happens without navigating away.
 */
function SectionPanel({ title, description, canEdit, addLabel, onAdd, children }: {
  title: string; description: string; canEdit: boolean; addLabel: string; onAdd: () => void; children: React.ReactNode;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--sp-3)' }}>
        <div>
          <h3 style={{ margin: '0 0 var(--sp-1)', fontSize: 'var(--fs-md)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>{title}</h3>
          <p style={{ margin: 0, fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)', maxWidth: '60ch' }}>{description}</p>
        </div>
        {canEdit && (
          <Button variant="primary" size="sm" onClick={onAdd}>+ {addLabel}</Button>
        )}
      </div>
      {children}
    </div>
  );
}

/**
 * A bills row: either a real bill, or a YEAR THAT HAS VALUES BUT NO BILL.
 *
 * (Operator feedback 8/6) "if there's a 2025 value then there should be a 2025 tax bill."
 * The tab used to render only the bills that exist, so a year with values and no bill simply
 * VANISHED — indistinguishable from a year nobody had worked. On the demo that reads as a
 * product fault when the real answer is that no 2025 bill was ever loaded.
 *
 * A placeholder is NOT a bill: nothing is created, no money is implied, and the row is not
 * clickable. It states the gap in the one place someone would look for it.
 */
type BillRow = TaxBill | { __missingYear: number };
const isMissing = (r: BillRow): r is { __missingYear: number } =>
  typeof (r as { __missingYear?: number }).__missingYear === 'number';

function BillsTab({ bills, loading, valueYears = [], onRowClick }: {
  bills: TaxBill[]; loading: boolean; valueYears?: number[]; onRowClick: (b: TaxBill) => void;
}) {
  // Real bills, plus one placeholder per value-year with no bill. Newest first so the
  // current year leads, which is the year an operator is nearly always looking for.
  const rows = useMemo<BillRow[]>(() => {
    const billed = new Set(bills.map((b) => b.tax_year));
    const gaps: BillRow[] = valueYears
      .filter((y) => Number.isFinite(y) && !billed.has(y))
      .map((y) => ({ __missingYear: y }));
    const yearOf = (r: BillRow) => (isMissing(r) ? r.__missingYear : r.tax_year);
    return [...bills, ...gaps].sort((a, b) => yearOf(b) - yearOf(a));
  }, [bills, valueYears]);

  const muted: React.CSSProperties = { color: 'var(--color-ink-3)', fontStyle: 'italic' };

  const columns = useMemo<ColumnDef<BillRow>[]>(() => [
    {
      id: 'tax_year',
      header: 'Year',
      cell: ({ row }) => (isMissing(row.original)
        ? <span style={muted}>{row.original.__missingYear}</span>
        : billYearLabel(row.original.tax_year, row.original.process_year)),
    },
    {
      id: 'tax_amount',
      header: 'Amount',
      cell: ({ row }) => {
        if (isMissing(row.original)) return <span style={muted}>No tax bill on file</span>;
        const v = row.original.tax_amount;
        return v != null ? `$${v.toLocaleString()}` : '—';
      },
    },
    { id: 'due_date', header: 'Due', cell: ({ row }) => (isMissing(row.original) ? <span style={muted}>—</span> : formatDate(row.original.due_date)) },
    { id: 'paid_date', header: 'Paid', cell: ({ row }) => (isMissing(row.original) ? <span style={muted}>—</span> : formatDate(row.original.paid_date)) },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => {
        if (isMissing(row.original)) return <Badge variant="warning">Not loaded</Badge>;
        const v = row.original.status;
        return v ? <Badge variant="neutral">{v.replace(/_/g, ' ')}</Badge> : <>—</>;
      },
    },
  ], []);

  if (loading) return <p style={{ color: 'var(--color-ink-3)' }}>Loading bills…</p>;
  if (rows.length === 0) return <Empty message="No tax bills on this parcel yet." />;
  return (
    <DataTable
      data={rows}
      columns={columns}
      // A placeholder has nothing to open — only a real bill navigates.
      onRowClick={(r) => { if (!isMissing(r)) onRowClick(r); }}
    />
  );
}

function RecordsTab({ records, loading, jurisdictionNames, onRowClick }: { records: TaxRecord[]; loading: boolean; jurisdictionNames: Record<string, string>; onRowClick: (r: TaxRecord) => void }) {
  const columns = useMemo<ColumnDef<TaxRecord>[]>(() => [
    // 🔴 YEAR FIRST. A parcel carries a jurisdiction line per YEAR, so the same jurisdiction
    // appears more than once — and without this column the rows are indistinguishable. On the
    // customer demo that reads as a duplicate line rather than two tax years, which is exactly
    // how it was reported. It leads the table because the year is what tells the rows apart.
    {
      accessorKey: 'tax_year',
      header: 'Year',
      cell: ({ getValue }) => {
        const y = getValue() as number | null;
        return y == null ? '—' : <span style={{ fontVariantNumeric: 'tabular-nums' }}>{y}</span>;
      },
    },
    { accessorKey: 'jurisdiction_id', header: 'Jurisdiction', cell: ({ getValue }) => jurisdictionNames[getValue() as string] ?? '—' },
    {
      accessorKey: 'rate',
      header: 'Rate',
      cell: ({ row }) => {
        const v = row.original.rate;
        return (
          <span style={{ display: 'inline-flex', gap: 'var(--sp-2)', alignItems: 'center' }}>
            {v != null ? `${v}%` : '—'}
            {/* RH§1 — flag a provisional (unconfirmed-rate) line as an estimate. */}
            {isEstimateLine(row.original.rate_is_estimate) && <Badge variant="warning">Estimate</Badge>}
          </span>
        );
      },
    },
    {
      id: 'tax_savings',
      header: 'Tax savings',
      /**
       * What this line's appeal saved: (beginning − current) × rate.
       *
       * The operator's basis, in their words (2026-08-07): "we just need to have the
       * proposed against the current value". `beginning_value` IS that proposed
       * figure — and because it honours the line's own "use alternate value" tick,
       * a line measured from an alternate basis shows the saving on THAT basis
       * without this column needing to know the difference.
       *
       * ⚠️ RATE IS A PERCENT HERE, hence ÷100. The Rate column one cell to the left
       * renders `${v}%`, and the engine's own arithmetic agrees — a 1.0578% Dallas
       * ISD line on 6,620,000 bills 70,026.36. Treating it as a millage would
       * overstate every saving on this screen by 10×, silently, in the column an
       * operator is most likely to read a number off.
       *
       * ⚠️ SHOWS "—" RATHER THAN ZERO when either end is missing. A line with no
       * baseline has an UNKNOWN saving, not a nil one, and a confident $0 next to a
       * real reduction is the reading that costs a fee. A money-blind role receives
       * neither figure (both are denylisted), so this is also what they see.
       */
      cell: ({ row }) => {
        const { beginning_value: from, current_value: to, rate } = row.original;
        if (from == null || to == null || rate == null) return '—';
        const saved = (Number(from) - Number(to)) * (Number(rate) / 100);
        if (!Number.isFinite(saved)) return '—';
        // A negative saving is real — a board can come back HIGHER — and is shown as
        // such rather than clamped, because hiding it would present an increase as
        // break-even on the screen where someone decides whether to bill.
        return (
          <span style={{ color: saved < 0 ? 'var(--color-status-red)' : undefined }}>
            {saved < 0 ? '-' : ''}${Math.abs(Math.round(saved)).toLocaleString()}
          </span>
        );
      },
    },
    {
      accessorKey: 'tax_amount',
      header: 'Tax',
      cell: ({ getValue }) => {
        const v = getValue() as number | null;
        return v != null ? `$${v.toLocaleString()}` : '—';
      },
    },
  ], [jurisdictionNames]);
  if (loading) return <p style={{ color: 'var(--color-ink-3)' }}>Loading jurisdiction lines…</p>;
  if (records.length === 0) return <Empty message="No jurisdiction tax lines on this parcel yet." />;
  // Newest year first, matching the Bills tab — the current year is the one an operator is
  // nearly always looking for, and an arbitrary order is what made two years read as duplicates.
  const rows = useMemo(
    () => [...records].sort((a, b) => (b.tax_year ?? 0) - (a.tax_year ?? 0)),
    [records],
  );
  return <DataTable data={rows} columns={columns} onRowClick={onRowClick} />;
}

/**
 * Unified parcel tax view (Yadi feedback): the tax bill and its tax records are
 * ONE thing, not two tabs. The collector's bill (amount due) sits above the
 * jurisdiction lines — each record applies a jurisdiction's rate to the parcel's
 * assessed value — that SUM into that amount due.
 */
function TaxesTab({ bills, records, billsLoading, recordsLoading, jurisdictionNames, valueYears = [], onBillClick, onRecordClick }: {
  bills: TaxBill[]; records: TaxRecord[]; billsLoading: boolean; recordsLoading: boolean;
  jurisdictionNames: Record<string, string>; valueYears?: number[];
  onBillClick: (b: TaxBill) => void; onRecordClick: (r: TaxRecord) => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)' }}>
      <section>
        <h3 style={{ margin: '0 0 var(--sp-2)', fontSize: '1rem', fontWeight: 600 }}>Tax bill</h3>
        <p style={{ margin: '0 0 var(--sp-3)', color: 'var(--color-ink-3)', fontSize: '0.85rem' }}>
          The collector's bill for this parcel. Amount due = the sum of the jurisdiction lines below.
        </p>
        <BillsTab bills={bills} loading={billsLoading} valueYears={valueYears} onRowClick={onBillClick} />
      </section>
      <section>
        <h3 style={{ margin: '0 0 var(--sp-2)', fontSize: '1rem', fontWeight: 600 }}>Jurisdiction lines</h3>
        <p style={{ margin: '0 0 var(--sp-3)', color: 'var(--color-ink-3)', fontSize: '0.85rem' }}>
          Each line applies a jurisdiction's rate to the parcel's assessed value; together they make up the bill.
        </p>
        <RecordsTab records={records} loading={recordsLoading} jurisdictionNames={jurisdictionNames} onRowClick={onRecordClick} />
      </section>
    </div>
  );
}

function InfoCard({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{
      background: 'var(--color-card)',
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--r-lg)',
      padding: 'var(--sp-4)',
    }}>
      <h3 style={{
        margin: '0 0 var(--sp-3)',
        fontSize: 'var(--fs-xs)',
        fontWeight: 'var(--fw-semibold)',
        textTransform: 'uppercase',
        letterSpacing: 'var(--ls-wider)',
        color: 'var(--color-ink-3)',
      }}>{label}</h3>
      {children}
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      gap: 'var(--sp-3)',
      padding: 'var(--sp-1) 0',
      fontSize: 'var(--fs-sm)',
      borderBottom: '1px solid var(--color-border)',
    }}>
      <span style={{ color: 'var(--color-ink-3)' }}>{label}</span>
      <span style={{ color: 'var(--color-ink)', fontWeight: 'var(--fw-medium)', textAlign: 'right', wordBreak: 'break-word', maxWidth: '60%' }}>{value}</span>
    </div>
  );
}

function Empty({ message }: { message: string }) {
  return (
    <div style={{
      padding: 'var(--sp-6)',
      background: 'var(--color-card)',
      border: '1px dashed var(--color-border)',
      borderRadius: 'var(--r-lg)',
      color: 'var(--color-muted)',
      textAlign: 'center',
      fontSize: 'var(--fs-md)',
    }}>{message}</div>
  );
}

/**
 * ParcelMapTab — where the parcel's SITE physically is.
 *
 * Coordinates live on `properties` (migration 134): the property is the physical
 * location, and several parcels routinely sit on one (a real-estate account and a
 * personal-property account against the same building). So this tab shows the
 * PARENT PROPERTY's location rather than a second, duplicate point — the same
 * convention the Documents tab already uses ("documents related to this parcel are
 * linked to this property"). Locating it here writes the point onto the property,
 * where every sibling parcel then reads it.
 */
function ParcelMapTab(
  { propertyId, accountNumber, county }:
  { propertyId: string; accountNumber: string | null; county: string | null },
) {
  // Hook before any early return (Rules-of-Hooks — this page's own history).
  const canEditProperty = useEntityPermission('property', 'can_edit');
  const propertyQuery = useQuery<ParcelSiteProperty>({
    queryKey: ['property', propertyId],
    queryFn: ({ signal }) => api.get<ParcelSiteProperty>(`/api/properties/${propertyId}`, signal),
    enabled: !!propertyId,
  });

  if (propertyQuery.isLoading) return <p style={{ color: 'var(--color-ink-3)' }}>Loading site…</p>;
  const property = propertyQuery.data;
  if (!property) {
    return <p style={{ color: 'var(--color-ink-3)' }}>This parcel's property could not be loaded, so there is no site to map.</p>;
  }

  return (
    <>
      <PropertyMapCard
        propertyId={property.id}
        name={property.name}
        addressLine1={property.address_line1}
        city={property.city}
        state={property.state}
        postalCode={property.postal_code}
        latitude={property.latitude}
        longitude={property.longitude}
        canEdit={canEditProperty}
        invalidateKeys={[['property', property.id], ['properties-list']]}
        // The join key for the boundary overlay. Absent or unmatched -> the
        // basemap renders alone; it never blocks the map.
        parcelAccountNumber={accountNumber}
        parcelCounty={county}
      />
      <p style={{ marginTop: 'var(--sp-2)', fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>
        The location is held on this parcel&rsquo;s property, so every parcel on the same
        site shares one point.
      </p>
    </>
  );
}

/** The subset of a property this tab reads — identity, address, coordinates. */
interface ParcelSiteProperty {
  id: string;
  name: string;
  address_line1: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  latitude: number | null;
  longitude: number | null;
}
